import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API_URL } from "@/config";
import { Gavel, Clock, Tag, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Listing {
    listing_id: number;
    title: string | null;
    year: number | null;
    make: string | null;
    model: string | null;
    mileage: string | null;
    condition: string | null;
    current_bid: string | null;
    buy_now_price: string | null;
    starting_bid: string | null;
    auction_mode: string;
    auction_end: string | null;
    listing_type: string;
    status: string;
    _count: { bids: number };
}

const MODE_BADGE: Record<string, { label: string; color: string }> = {
    TIMED:   { label: "Auction",  color: "bg-violet-100 text-violet-700" },
    BUY_NOW: { label: "Buy Now",  color: "bg-green-100 text-green-700"   },
    OFFER:   { label: "Make Offer", color: "bg-blue-100 text-blue-700"  },
};

function countdown(end: string): string {
    const ms = new Date(end).getTime() - Date.now();
    if (ms <= 0) return "Ended";
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    return h > 24 ? `${Math.floor(h / 24)}d ${h % 24}h` : `${h}h ${m}m`;
}

export default function AuctionMarketplacePage() {
    const [listings, setListings] = useState<Listing[]>([]);
    const [type,     setType]     = useState<"PUBLIC" | "INTERNAL">("PUBLIC");
    const [loading,  setLoading]  = useState(false);

    const fetchListings = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/auction/listings?type=${type}&status=ACTIVE`, { withCredentials: true });
            setListings(res.data.listings ?? []);
        } catch {
            setListings([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchListings(); }, [type]);

    return (
        <div className="max-w-6xl mx-auto py-8 px-4 space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <Gavel className="h-6 w-6 text-indigo-600" />
                        Auction Marketplace
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">Bid, buy, or make offers on available vehicles.</p>
                </div>
                <div className="flex gap-2">
                    <Button
                        variant={type === "PUBLIC" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setType("PUBLIC")}
                    >
                        Public
                    </Button>
                    <Button
                        variant={type === "INTERNAL" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setType("INTERNAL")}
                    >
                        Internal
                    </Button>
                </div>
            </div>

            {loading && <p className="text-center text-muted-foreground">Loading listings…</p>}

            {!loading && listings.length === 0 && (
                <div className="text-center py-16">
                    <Gavel className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                    <p className="text-muted-foreground">No active listings at the moment. Check back soon!</p>
                </div>
            )}

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {listings.map((l) => {
                    const badge = MODE_BADGE[l.auction_mode] ?? { label: l.auction_mode, color: "bg-gray-100 text-gray-700" };
                    const vehicle = [l.year, l.make, l.model].filter(Boolean).join(" ") || l.title || "Vehicle";
                    return (
                        <Link
                            key={l.listing_id}
                            to={`/auctions/listings/${l.listing_id}`}
                            className="bg-card rounded-xl border p-5 space-y-3 hover:shadow-md transition block"
                        >
                            <div className="flex items-start justify-between gap-2">
                                <h3 className="font-semibold text-sm line-clamp-2">{vehicle}</h3>
                                <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium ${badge.color}`}>
                                    {badge.label}
                                </span>
                            </div>

                            {l.mileage && (
                                <p className="text-xs text-muted-foreground">{l.mileage} miles · {l.condition ?? "—"}</p>
                            )}

                            <div className="flex items-center justify-between text-sm">
                                {l.auction_mode === "TIMED" && (
                                    <div>
                                        <p className="text-xs text-muted-foreground">Current Bid</p>
                                        <p className="font-bold text-base">${l.current_bid ?? l.starting_bid ?? "—"}</p>
                                    </div>
                                )}
                                {l.auction_mode === "BUY_NOW" && (
                                    <div>
                                        <p className="text-xs text-muted-foreground">Buy Now</p>
                                        <p className="font-bold text-base text-green-700">${l.buy_now_price ?? "—"}</p>
                                    </div>
                                )}
                                {l.auction_mode === "OFFER" && (
                                    <div>
                                        <p className="text-xs text-muted-foreground">Starting At</p>
                                        <p className="font-bold text-base">${l.starting_bid ?? "—"}</p>
                                    </div>
                                )}
                                <div className="text-right">
                                    <p className="text-xs text-muted-foreground flex items-center gap-1 justify-end">
                                        <TrendingUp className="h-3 w-3" /> {l._count.bids} bids
                                    </p>
                                    {l.auction_end && (
                                        <p className="text-xs text-muted-foreground flex items-center gap-1 justify-end mt-0.5">
                                            <Clock className="h-3 w-3" /> {countdown(l.auction_end)}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="pt-2 border-t">
                                <span className="text-xs font-medium text-indigo-600">View Listing →</span>
                            </div>
                        </Link>
                    );
                })}
            </div>
        </div>
    );
}
